import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { COLOR_SYSTEM } from "components/design/design-system";
import Icon from "components/atoms/Icon";
import Text from "components/atoms/Text";
import Button from "components/atoms/Button";

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  padding: 20px;
`;

const ModalContainer = styled.div`
  background: ${COLOR_SYSTEM.get("Gray")?.[0]};
  border-radius: 16px;
  max-width: 95vw;
  max-height: 90vh;
  width: 1000px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
`;

const ModalHeader = styled.div`
  background-color: ${COLOR_SYSTEM.get("Gray")?.[50]};
  padding: 12px 24px 12px 24px;
  border-bottom: 1px solid ${COLOR_SYSTEM.get("Gray")?.[100]};
`;

const HeaderTop = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ProgressContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const ProgressBar = styled.div`
  flex: 1;
  height: 4px;
  background: ${COLOR_SYSTEM.get("Gray")?.[200]};
  border-radius: 2px;
  overflow: hidden;
`;

const ProgressFill = styled.div<{ progress: number }>`
  height: 100%;
  background: ${COLOR_SYSTEM.get("Blue")?.[400]};
  border-radius: 2px;
  width: ${(props) => props.progress}%;
  transition: width 0.3s ease;
`;

const ProgressText = styled.div`
  font-size: 12px;
  color: ${COLOR_SYSTEM.get("Gray")?.[600]};
  white-space: nowrap;
`;

const ModalBody = styled.div`
  display: flex;
  flex: 1;
  overflow: hidden;
`;

const TableOfContents = styled.div`
  width: 240px;
  background: ${COLOR_SYSTEM.get("Gray")?.[50]};
  border-right: 1px solid ${COLOR_SYSTEM.get("Gray")?.[200]};
  padding: 16px;
  overflow-y: auto;
`;

const CategoryHeader = styled.div`
  font-size: 12px;
  font-weight: 700;
  color: ${COLOR_SYSTEM.get("Gray")?.[500]};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 8px;
  margin-top: 16px;
  padding-left: 4px;
  
  &:first-child {
    margin-top: 0;
  }
`;

const CategoryDivider = styled.hr`
  border: none;
  border-top: 1px solid ${COLOR_SYSTEM.get("Gray")?.[200]};
  margin: 16px 0;
  width: 100%;
`;

const TOCItem = styled.div<{ active: boolean }>`
  padding: 8px 12px;
  margin-bottom: 6px;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
  background: ${(props) =>
    props.active ? COLOR_SYSTEM.get("Blue")?.[50] : "transparent"};
  border: 1px solid ${(props) =>
    props.active ? COLOR_SYSTEM.get("Blue")?.[200] : "transparent"};

  &:hover {
    background: ${(props) =>
      props.active ? COLOR_SYSTEM.get("Blue")?.[50] : COLOR_SYSTEM.get("Gray")?.[100]};
  }
`;

const TOCTitle = styled.div<{ active: boolean }>`
  font-weight: 600;
  color: ${(props) =>
    props.active ? COLOR_SYSTEM.get("Blue")?.[700] : COLOR_SYSTEM.get("Gray")?.[800]};
  margin-bottom: 4px;
  font-size: 14px;
  text-align: left;
`;

const TOCSubtitle = styled.div<{ active: boolean }>`
  font-size: 12px;
  color: ${(props) =>
    props.active ? COLOR_SYSTEM.get("Blue")?.[600] : COLOR_SYSTEM.get("Gray")?.[600]};
  text-align: left;
`;

const SlideContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 500px;
  background: ${COLOR_SYSTEM.get("Gray")?.[25]};
  padding: 16px;
`;

const ImageWrapper = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
  max-height: calc(100vh - 300px);
  overflow: hidden;
`;

const HintText = styled.div`
  text-align: center;
  font-size: 13px;
  color: ${COLOR_SYSTEM.get("Gray")?.[500]};
  margin-bottom: 12px;
  padding: 8px;
`;

const NavigationButtons = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 24px;
  padding: 0 20px;
`;

const SlideImage = styled.img`
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  border-radius: 8px;
  cursor: pointer;
  transition: transform 0.2s ease;

  &:hover {
    transform: scale(1.02);
  }
`;

const ExpandedImageOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 20000;
  cursor: pointer;
`;

const ExpandedImage = styled.img`
  max-width: 95vw;
  max-height: 95vh;
  object-fit: contain;
  border-radius: 8px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.5);
`;

const CloseExpandedButton = styled.button`
  position: absolute;
  top: 20px;
  right: 20px;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: none;
  background: rgba(255, 255, 255, 0.2);
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.3);
  }
`;

const ExpandedNavButton = styled.button<{ disabled?: boolean }>`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 48px;
  height: 48px;
  border-radius: 50%;
  border: none;
  background: rgba(255, 255, 255, 0.2);
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  opacity: ${(props) => (props.disabled ? 0.3 : 1)};

  &:hover:not(:disabled) {
    background: rgba(255, 255, 255, 0.3);
    transform: translateY(-50%) scale(1.05);
  }

  &:disabled {
    cursor: not-allowed;
  }

  &.prev {
    left: 20px;
  }

  &.next {
    right: 20px;
  }
`;

const ExpandedPageInfo = styled.div`
  position: absolute;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(0, 0, 0, 0.7);
  color: white;
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 14px;
  white-space: nowrap;
`;







interface Section {
  name: string;
  range: [number, number];
  category: string;
  version: string;
}

const sections: Section[] = [
  { name: "새 환자 등록하기", range: [3, 11], category: "필수 가이드", version: "20250901-3" },
  { name: "환자 정보 입력하기", range: [12, 31], category: "필수 가이드", version: "20250901-12" },
  { name: "AI 분석 시키기", range: [32, 37], category: "필수 가이드", version: "20250901-17" },
  { name: "AI가 생성한 검토안 평가하기", range: [38, 50], category: "필수 가이드", version: "20250901-23" },
  { name: "요금안내", range: [51, 56], category: "부가기능 안내", version: "20250901-36" },
  { name: "보고서 편집 기능", range: [57, 80], category: "부가기능 안내", version: "20250901-42" },
  { name: "인쇄 기능", range: [81, 93], category: "부가기능 안내", version: "20250901-66" },
  { name: "25.10 환자용 복약지도문 추가", range: [94, 102], category: "업데이트 공지", version: "20251021-94" },
  { name: "25.11 안전관리원 정보집 추가", range: [103, 107], category: "업데이트 공지", version: "20251121-103" },
  { name: "26.04 검토보고서 국제표준 개선", range: [108, 120], category: "업데이트 공지", version: "20260408-108" },
  { name: "26.04 건기식 DB추가", range: [121, 127], category: "업데이트 공지", version: "20260408-121" },
  { name: "26.06 약사 편의기능 추가", range: [128, 164], category: "업데이트 공지", version: "20260623-128" }
];

// 전체 가이드의 최신 버전 (섹션 중 최대 버전의 날짜 부분)
export const getManualVersion = (): string => {
  const maxVersion = sections.reduce((max, section) => {
    return section.version > max ? section.version : max;
  }, "");
  return maxVersion.split("-")[0]; // "20251021-79" → "20251021"
};

// 특정 버전의 섹션 첫 페이지 찾기
export const getPageByVersion = (version: string): number => {
  const section = sections.find(s => s.version === version);
  if (section) {
    // 해당 섹션의 시작 페이지 (0-based index)
    return section.range[0] - 1;
  }
  // 해당 버전이 없으면 첫 페이지
  return 0;
};

// 사용자 버전보다 큰 날짜의 첫 번째 섹션의 시작 페이지 찾기
export const getStartPageForUser = (userManualVersion?: string): number => {
  if (!userManualVersion) {
    // 신규 사용자: 처음부터
    return 0;
  }

  // 사용자 버전의 날짜 부분 추출
  const userVersionDate = userManualVersion.split("-")[0];

  // 사용자 버전의 날짜보다 큰 날짜를 가진 첫 번째 섹션 찾기
  const nextSection = sections.find(section => {
    const sectionDate = section.version.split("-")[0];
    return sectionDate > userVersionDate;
  });

  if (nextSection) {
    // 해당 섹션의 시작 페이지 (0-based index)
    return nextSection.range[0] - 1;
  }

  // 모든 섹션을 이미 봤으면 첫 페이지
  return 0;
};

interface UserManualModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialSlide?: number;
}

const UserManualModal: React.FC<UserManualModalProps> = ({ isOpen, onClose, initialSlide = 0 }) => {
  const [currentSlide, setCurrentSlide] = useState(initialSlide);
  const [isImageExpanded, setIsImageExpanded] = useState(false);
  const totalSlides = 164;

  // initialSlide가 변경되면 currentSlide 업데이트
  useEffect(() => {
    setCurrentSlide(initialSlide);
  }, [initialSlide]);

  // 현재 페이지가 속한 구간 찾기
  const getCurrentSection = () => {
    const currentPage = currentSlide + 1;
    return sections.findIndex(section => 
      currentPage >= section.range[0] && currentPage <= section.range[1]
    );
  };

  // 현재 구간의 진행률 계산
  const getCurrentSectionProgress = () => {
    const currentPage = currentSlide + 1;
    const sectionIndex = getCurrentSection();
    if (sectionIndex === -1) return { current: 1, total: 1 };
    
    const section = sections[sectionIndex];
    const current = currentPage - section.range[0] + 1;
    const total = section.range[1] - section.range[0] + 1;
    return { current, total };
  };

  // 구간별 도트 클릭 핸들러
  const handleSectionClick = (sectionIndex: number) => {
    const targetPage = sections[sectionIndex].range[0] - 1; // 0-based index
    setCurrentSlide(targetPage);
  };

  // 이미지 확대/축소 핸들러
  const handleImageClick = () => {
    setIsImageExpanded(true);
  };

  const handleExpandedImageClose = () => {
    setIsImageExpanded(false);
  };

  // 키보드 이벤트 처리
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (isImageExpanded) {
        if (e.key === "Escape") {
          handleExpandedImageClose();
        } else if (e.key === "ArrowLeft") {
          handlePrevSlide();
        } else if (e.key === "ArrowRight") {
          handleNextSlide();
        }
        return;
      }

      if (e.key === "ArrowLeft") {
        handlePrevSlide();
      } else if (e.key === "ArrowRight") {
        handleNextSlide();
      } else if (e.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, currentSlide, isImageExpanded]);

  // 모달 외부 클릭 시 닫기
  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handlePrevSlide = () => {
    setCurrentSlide((prev) => (prev > 0 ? prev - 1 : prev));
  };

  const handleNextSlide = () => {
    if (currentSlide < totalSlides - 1) {
      setCurrentSlide((prev) => prev + 1);
    } else {
      // 마지막 페이지에서 다음 버튼을 누르면 첫 페이지로 리셋 후 모달 닫기
      setCurrentSlide(0);
      onClose();
    }
  };



  if (!isOpen) return null;

  return (
    <>
      <ModalOverlay onClick={handleOverlayClick}>
        <ModalContainer>
        <ModalHeader>
          <HeaderTop>
            <Text textType="H4" color={COLOR_SYSTEM.get("Gray")?.[800]}>
              PhAI 사용법
            </Text>
            <Button
              type="Icon"
              size="Medium"
              variant="Ghost"
              color="Tertiary"
              icon="X"
              onClick={onClose}
            />
          </HeaderTop>
          
          <ProgressContainer>
            <ProgressBar>
              <ProgressFill progress={(currentSlide + 1) / totalSlides * 100} />
            </ProgressBar>
            <ProgressText>
              {currentSlide + 1} / {totalSlides}
            </ProgressText>
          </ProgressContainer>
        </ModalHeader>

        <ModalBody>
          <TableOfContents>
            {["필수 가이드", "부가기능 안내", "업데이트 공지"].map((category, categoryIndex) => (
              <div key={category}>
                {categoryIndex > 0 && <CategoryDivider />}
                <CategoryHeader>{category}</CategoryHeader>
                {sections
                  .filter(section => section.category === category)
                  .map((section, sectionIndex) => {
                    const index = sections.indexOf(section);
                    const isActive = index === getCurrentSection();
                    const progress = isActive ? getCurrentSectionProgress() : null;

                    return (
                      <TOCItem
                        key={index}
                        active={isActive}
                        onClick={() => handleSectionClick(index)}
                      >
                        <TOCTitle active={isActive}>
                          • {section.name}
                        </TOCTitle>
                        <TOCSubtitle active={isActive}>
                          {section.range[0] === section.range[1]
                            ? `${section.range[0]}페이지`
                            : `${section.range[0]}-${section.range[1]}페이지`}
                          {isActive && progress && ` (${progress.current}/${progress.total})`}
                        </TOCSubtitle>
                      </TOCItem>
                    );
                  })}
              </div>
            ))}
          </TableOfContents>

          <SlideContainer>
            <ImageWrapper>
              <SlideImage
                src={`/images/guide/guide-${String(currentSlide + 1).padStart(2, "0")}.png`}
                alt={`사용법 ${currentSlide + 1}페이지`}
                onClick={handleImageClick}
                onError={(e) => {
                  // 이미지 로드 실패 시 대체 텍스트 표시
                  console.warn(`가이드 이미지 ${currentSlide + 1}을 불러올 수 없습니다.`);
                }}
              />
            </ImageWrapper>

            <HintText>
              이미지를 클릭하면 전체화면으로 크게 볼 수 있습니다
            </HintText>

            <NavigationButtons>
              {currentSlide === 49 ? ( // 50페이지 (0-based index이므로 49)
                <>
                  <Button
                    type="Icon"
                    size="Medium"
                    variant="Outlined"
                    color="Primary"
                    icon="Caret Left"
                    onClick={handlePrevSlide}
                    style={{
                      borderRadius: "25%",
                      backgroundColor: COLOR_SYSTEM.get("Gray")?.[0],
                      flexShrink: 0
                    }}
                  />
                  <Button
                    type="Text"
                    size="Medium"
                    variant="Outlined"
                    color="Secondary"
                    onClick={onClose}
                  >
                    닫기
                  </Button>
                  <Button
                    type="Text"
                    size="Medium"
                    variant="Contained"
                    color="Primary"
                    onClick={() => setCurrentSlide(50)} // 51페이지로 이동 (부가기능 시작)
                  >
                    부가기능 알아보기
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    type="Icon"
                    size="Medium"
                    variant="Outlined"
                    color="Primary"
                    icon="Caret Left"
                    onClick={handlePrevSlide}
                    disabled={currentSlide === 0}
                    style={{
                      borderRadius: "25%",
                      backgroundColor: COLOR_SYSTEM.get("Gray")?.[0],
                      flexShrink: 0
                    }}
                  />

                  <Button
                    type="Icon"
                    size="Medium"
                    variant="Outlined"
                    color="Primary"
                    icon={currentSlide === totalSlides - 1 ? "X" : "Caret Right"}
                    onClick={handleNextSlide}
                    style={{
                      borderRadius: "25%",
                      backgroundColor: COLOR_SYSTEM.get("Gray")?.[0],
                      flexShrink: 0
                    }}
                  />
                </>
              )}
            </NavigationButtons>
          </SlideContainer>
        </ModalBody>


              </ModalContainer>
      </ModalOverlay>

      {/* 확대 이미지 오버레이 */}
      {isImageExpanded && (
        <ExpandedImageOverlay onClick={handleExpandedImageClose}>
          <CloseExpandedButton onClick={handleExpandedImageClose}>
            <Icon name="X" size={20} color="white" />
          </CloseExpandedButton>
          
          <ExpandedNavButton
            className="prev"
            onClick={(e) => {
              e.stopPropagation();
              handlePrevSlide();
            }}
            disabled={currentSlide === 0}
          >
            <Icon name="Caret Left" size={24} color="white" />
          </ExpandedNavButton>

          <ExpandedImage
            src={`/images/guide/guide-${String(currentSlide + 1).padStart(2, "0")}.png`}
            alt={`사용법 ${currentSlide + 1}페이지 확대`}
            onClick={(e) => e.stopPropagation()} // 이미지 클릭 시 오버레이 닫기 방지
          />

          <ExpandedNavButton
            className="next"
            onClick={(e) => {
              e.stopPropagation();
              currentSlide === totalSlides - 1 ? handleExpandedImageClose() : handleNextSlide();
            }}
          >
            <Icon 
              name={currentSlide === totalSlides - 1 ? "X" : "Caret Right"} 
              size={24} 
              color="white" 
            />
          </ExpandedNavButton>

          <ExpandedPageInfo>
            {currentSlide + 1} / {totalSlides}
          </ExpandedPageInfo>
        </ExpandedImageOverlay>
      )}
    </>
  );
};

export default UserManualModal; 